import React from 'react';

import { LayoutSingleColumn, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { useConfiguration } from '../../context/configurationContext.js';

import css from './CommunityPage.module.css';

/**
 * CommunityPage component renders the Booker’s Reading Club coming-soon page.
 */
const CommunityPage = props => {
  const config = useConfiguration();
  const instagramUrl = config.siteInstagramPage || 'https://www.instagram.com/shelfy.books/';
  const features = [
    [
      'Read together',
      'We announce a new book every month, with plenty of time to read at your own pace.',
    ],
    [
      'Join the conversation',
      'Share your thoughts with fellow Canadian readers in a welcoming discussion space.',
    ],
    ['Look forward to more', 'Vote on future picks, enjoy bonus content, and help shape the club.'],
  ];
  const heroHighlights = [
    ['Monthly pick', 'A new book to read together every month.'],
    ['Reader discussion', 'Meaningful conversations with fellow readers.'],
    ['Check-ins', 'Prompts, reactions, and mid-month discussion.'],
    ['Future polls', 'Help shape what we read next.'],
    ['Bonus content', 'PageHop extras, themed recs, and more.'],
  ];

  return (
    <Page
      title="Booker’s Reading Club | PageHop Books"
      description="A monthly book club for Canadian readers who want great picks, thoughtful discussion, and a little extra shelf obsession."
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.root}>
          <section className={css.heroSection}>
            <div className={css.heroInner}>
              <div className={css.heroCopy}>
                <p className={css.eyebrow}>A community of Canadian readers</p>
                <h1 className={css.heroTitle}>Booker’s Reading Club</h1>
                <p className={css.heroDescription}>
                  A monthly book club for Canadian readers who love great stories, thoughtful
                  discussion, and a little extra shelf obsession. We&apos;re building something
                  social, flexible, and very bookish, and we&apos;d love for you to be part of it.
                </p>
                <div className={css.heroCtaGroup}>
                  <a
                    href={instagramUrl}
                    className={css.heroCta}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Join the waitlist <span aria-hidden="true">-&gt;</span>
                  </a>
                  <img
                    className={css.booker}
                    src="/static/Happy-Booker.png"
                    alt="Booker, the Booker’s Reading Club character"
                  />
                </div>
              </div>
              <div className={css.bookStage} aria-label="Featured book covers">
                <div className={css.bookStack}>
                  <img
                    className={css.stackBookBack}
                    src="/static/book-club/book-lovers.jpg"
                    alt="Book Lovers cover"
                  />
                  <img
                    className={css.stackBookSide}
                    src="/static/book-club/fever-dream.jpg"
                    alt="Fever Dream cover"
                  />
                  <img
                    className={css.stackBookMain}
                    src="/static/book-club/yesteryear.jpg"
                    alt="Yesteryear cover"
                  />
                  <span className={css.pickBadge}>
                    Our
                    <br />
                    monthly
                    <br />
                    pick
                  </span>
                </div>
                <div className={css.stackCaption}>
                  <span>Our monthly pick</span>
                  <strong>Yesteryear</strong>
                </div>
              </div>
              <aside className={css.featureCluster} aria-label="Booker’s Reading Club benefits">
                <h2>More than just a book club</h2>
                <ul className={css.featureCards}>
                  {heroHighlights.map(([title, description]) => (
                    <li key={title} className={css.featureCard}>
                      <strong>{title}</strong>
                      <span>{description}</span>
                    </li>
                  ))}
                </ul>
              </aside>
            </div>
          </section>

          <section className={css.featuresSection} aria-labelledby="club-features-title">
            <div className={css.sectionIntro}>
              <h2 id="club-features-title">A new chapter every month</h2>
              <p>
                Good books are better when there&apos;s someone to talk to about them. Each month
                we&apos;ll choose a new read, share discussion prompts, and create space for real
                conversations.
              </p>
            </div>
            <div className={css.featureGrid}>
              {features.map(([title, description]) => (
                <article className={css.featureItem} key={title}>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </article>
              ))}
            </div>
          </section>

          <section className={css.closingSection}>
            <div className={css.quotePanel}>
              <span className={css.quoteMark}>&ldquo;</span>
              <p>
                &ldquo;A good book gives you something to read. A great book gives you something to
                talk about.&rdquo;
              </p>
              <small>Booker’s Reading Club</small>
            </div>
            <div className={css.closingCopy}>
              <p className={css.eyebrow}>Canadian readers. Bigger stories.</p>
              <h2>Why join Booker’s Reading Club?</h2>
              <p>
                Come for the books, stay for the book people. Find new favourites, share the
                experience, and help shape a positive Canadian reading community.
              </p>
            </div>
          </section>
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

export default CommunityPage;
